import {
  SubtleButton,
  pushToDataLayer,
  theme,
  Tooltip,
  DeprecatedChevronDownIcon,
  DeprecatedChevronUpIcon,
} from '@arnold/common';
import styled from '@emotion/styled/macro';
import { FC, useMemo, useState, HTMLAttributes } from 'react';
import auth from '../../lib/auth';
import { omit } from 'ramda';
import StatementChart from './StatementChart';
import { BarChartBody, ChartBar, ChartBarBox, TooltipContent, TooltipItem } from './StyledComponents';
import { processStatements, Statement } from '../../lib/reports/statementHelpers';
import { useTranslation } from 'react-i18next';
import { IReport } from '../../lib/reports/base';
import {
  ChartOption,
  ColorCircle,
  getAColorByHighlightLevel,
  OptionItem,
  OptionName,
  StatementsHeader,
  StatementsHeaderContent,
} from './ScaleQuestionWidget';
import { ResponsiveSwitcher } from './ResponsiveSwitcher';
import { TeamWithType } from '../../lib/reports/queryAggregatedData';
import { Filter } from '../../lib/reportHelper';
import { AggregatedData } from '../../lib/reports/aggregateData';
import PulserChart from './PulserChart';
import {
  AnswerResult,
  calculateTeamQuestionStatistics,
  calculateAggregateStatistics,
  hasTeamData,
  AggregateStatistics,
} from './pulserHelpers';

interface IProps {
  questionText: string;
  percentage: number;
  notRelevantTotalCount: number;
  sectionReport: IReport;
  question: NonNullable<IReport['questions']>[0];
  answerResults: AnswerResult[];
  isLast: boolean;
  teamsToShow?: TeamWithType[] | undefined;
  compareLevelFilter?: Filter | undefined;
  aggregatedData?: AggregatedData | null;
  renderHeaderOnly?: boolean;
  categoryPosition?: number;
  organizationId?: string;
}

const PulserQuestionItem: FC<IProps & HTMLAttributes<HTMLDivElement>> = ({
  questionText,
  percentage,
  notRelevantTotalCount,
  answerResults,
  sectionReport,
  question,
  isLast,
  teamsToShow,
  compareLevelFilter,
  aggregatedData,
  renderHeaderOnly,
  categoryPosition,
  organizationId,
  ...rest
}) => {
  const { t } = useTranslation('Report');
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => {
    if (!isOpen) {
      const userId = auth.getUserId();
      pushToDataLayer({
        event: 'category_expanded',
        hit: {
          event_type: 'generic_event',
          action_name: 'expand',
          ...(userId && { user_id: userId }),
          ...(organizationId && { organization_id: organizationId }),
          category_name: questionText,
          category_score_pct: percentage,
          ...(categoryPosition !== undefined && { category_position: categoryPosition }),
        },
        _clear: true,
      });
    }
    setIsOpen(!isOpen);
  };

  const statements = processStatements(
    question.statementsLocal ?? [],
    question.index || '',
    sectionReport,
    t('ANONYMOUS'),
  );

  const chartOptions: ChartOption[] =
    question.answersLocal?.map((option, index) => ({
      optionName: option || '',
      highLightLevel: 0,
      index,
    })) || [];

  question.answersToHighlight?.forEach((highlightLvlIndex: string) => {
    chartOptions[parseInt(highlightLvlIndex, 10)].highLightLevel++;
  });

  const sortedChartOptions = [...chartOptions].sort((a, b) => b.highLightLevel - a.highLightLevel);

  // Memoize team statistics calculation to avoid recalculating on every render
  const teamStatistics = useMemo((): Array<{
    team: TeamWithType;
    statistics: AggregateStatistics;
    hasData: boolean;
  }> | null => {
    if (!teamsToShow || !aggregatedData) return null;

    return teamsToShow.map((team) => {
      const teamResponses = calculateTeamQuestionStatistics(question, sectionReport, team, aggregatedData);
      const teamAverages = calculateAggregateStatistics([teamResponses]);

      return {
        team,
        statistics: teamAverages,
        hasData: hasTeamData(teamAverages),
      };
    });
  }, [teamsToShow, aggregatedData, question, sectionReport]);

  const headerNode = (
    <ResponsiveSwitcher
      mobile={
        <QuestionHeader className={'d-flex flex-grow-1 py-7 justify-content-between'} onClick={toggleOpen}>
          <div className="flex-grow-1">
            <div className="d-flex flex-grow-1 justify-content-between">
              <WidgetQuestionHeading className={'mb-4'} data-pptx-id="report-intro-title">
                {questionText}
              </WidgetQuestionHeading>
              {!teamStatistics && (
                <SmallPercentageValue data-no-break className={'text-center mr-0'}>
                  {percentage.toFixed(0)}%
                </SmallPercentageValue>
              )}
            </div>
            {!teamStatistics && (
              <BarChartBody width="100%">
                {answerResults
                  .filter((answer) => answer.percentage > 0)
                  .map((answer, index, array) => {
                    const previousLevels = array.slice(0, index).filter((a) => a.level === answer.level);
                    const percent = answer.percentage;
                    const color =
                      theme.colors.chart[answer.level as keyof typeof theme.colors.chart][previousLevels.length];
                    return (
                      <ChartBarBox key={index} width={percent} hideMobileMarginTop>
                        <ChartBar color={color} height="8px"></ChartBar>
                      </ChartBarBox>
                    );
                  })}
              </BarChartBody>
            )}
            {teamStatistics && teamStatistics.length > 0 && (
              <ChartWrapper>
                {teamStatistics.map(({ team, statistics, hasData }) => {
                  if (!hasData) return null;

                  return (
                    <TeamWrapper key={team.team.id} mobile={true}>
                      <div style={{ width: '30%' }}>{team.team.teamName}</div>
                      <div className="d-flex justify-content-between w-100 align-items-baseline">
                        <PulserChart
                          answerResults={statistics.answers}
                          notRelevantTotalCount={statistics.nonRelevantTotalCount}
                          showEscapeOption={question.showEscapeOption ?? true}
                          percentage={statistics.averagePercentage}
                          percentageMargin="small"
                        />
                      </div>
                    </TeamWrapper>
                  );
                })}
              </ChartWrapper>
            )}
          </div>

          <SubtleButton
            onClick={toggleOpen}
            size="small"
            leftIcon={isOpen ? <DeprecatedChevronUpIcon /> : <DeprecatedChevronDownIcon />}
            iconColor={theme.colors.text.secondary}
            style={{ height: 'fit-content', marginLeft: theme.spacing.f }}
          />
        </QuestionHeader>
      }
      desktop={
        <QuestionHeader
          className={`d-flex flex-grow-1 py-7 ${
            teamsToShow && teamsToShow.length > 0 ? '' : 'justify-content-between'
          }`}
          onClick={toggleOpen}
          data-pulser-question-item-index={question.index}
          data-pulser-question-item-is-open={isOpen}
          data-pulser-question-item-is-closed={!isOpen}
        >
          <IconTextWrapper>
            <WidgetQuestionHeading data-no-break className={'mb-4'} data-pptx-id="report-intro-title">
              {questionText}
            </WidgetQuestionHeading>
          </IconTextWrapper>

          <div
            className="d-flex align-items-center"
            style={{ width: teamsToShow && teamsToShow.length > 0 ? '73%' : 'auto' }}
          >
            {!teamStatistics && (
              <>
                <Tooltip
                  delay={100}
                  followCursor={false}
                  html={
                    <TooltipContent chartOptions={answerResults}>
                      {answerResults.map((option, index, array) => {
                        const count = option.responses;
                        const previousLevels = array.slice(0, index).filter((a) => a.level === option.level);
                        const highlightColor =
                          theme.colors.chart[option.level as keyof typeof theme.colors.chart][previousLevels.length];
                        const percent = option.percentage;
                        return (
                          <TooltipItem key={index}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: theme.spacing.c,
                              }}
                            >
                              <div
                                style={{
                                  width: 8,
                                  height: 8,
                                  backgroundColor: highlightColor,
                                  borderRadius: '50%',
                                }}
                              ></div>
                              <div data-no-break style={{ fontWeight: 700 }}>
                                {percent.toFixed(0)}%
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, marginLeft: theme.spacing.e }}>
                              {t('PulserReport:numberOfAnswers', { count, postProcess: 'interval' })}
                            </div>
                            <div style={{ marginLeft: theme.spacing.e }}>{option.answer}</div>
                          </TooltipItem>
                        );
                      })}
                      {(question.showEscapeOption ?? true) && (
                        <TooltipItem>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: theme.spacing.c,
                            }}
                          >
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                backgroundColor: theme.colors.text.secondary,
                                borderRadius: '50%',
                              }}
                            ></div>
                            <div style={{ fontWeight: 700 }}>
                              {t('PulserReport:numberOfAnswers', {
                                count: notRelevantTotalCount,
                                postProcess: 'interval',
                              })}
                            </div>
                          </div>
                          <div style={{ marginLeft: theme.spacing.e }}>{t('bubble:scaleEscapeOption')}</div>
                        </TooltipItem>
                      )}
                    </TooltipContent>
                  }
                >
                  <BarChartBody width="182px">
                    {answerResults
                      .filter((answer) => answer.percentage > 0)
                      .map((answer, index, array) => {
                        const previousLevels = array.slice(0, index).filter((a) => a.level === answer.level);
                        const percent = answer.percentage;
                        const color =
                          theme.colors.chart[answer.level as keyof typeof theme.colors.chart][previousLevels.length];
                        return (
                          <ChartBarBox key={index} width={percent} hideMobileMarginTop>
                            <ChartBar color={color} height="8px"></ChartBar>
                          </ChartBarBox>
                        );
                      })}
                  </BarChartBody>
                </Tooltip>
                <SmallPercentageValue data-no-break className={'text-center'}>
                  {percentage.toFixed(0)}%
                </SmallPercentageValue>
              </>
            )}
            {teamStatistics && teamStatistics.length > 0 && (
              <ChartWrapper>
                {teamStatistics.map(({ team, statistics, hasData }) => {
                  if (!hasData) return null;

                  return (
                    <TeamWrapper key={team.team.id} data-cy="report-pulser-question-item-team">
                      <div style={{ width: '30%' }} data-cy="report-pulser-question-item-team-name">
                        {team.team.teamName}
                      </div>
                      <PulserChart
                        answerResults={statistics.answers}
                        notRelevantTotalCount={statistics.nonRelevantTotalCount}
                        showEscapeOption={question.showEscapeOption ?? true}
                        percentage={statistics.averagePercentage}
                        percentageMargin="small"
                      />
                    </TeamWrapper>
                  );
                })}
              </ChartWrapper>
            )}
            <SubtleButton
              onClick={toggleOpen}
              data-pptx-hide
              data-cy="report-pulser-question-item-toggle-button"
              size="small"
              leftIcon={isOpen ? <DeprecatedChevronUpIcon /> : <DeprecatedChevronDownIcon />}
              iconColor={theme.colors.text.secondary}
            />
          </div>
        </QuestionHeader>
      }
    />
  );

  if (renderHeaderOnly) {
    return <div {...rest}>{headerNode}</div>;
  }

  return (
    <QuestionWrapper className={isLast ? '' : 'border-bottom'} data-cy="report-pulser-question-item" {...rest}>
      {headerNode}
      {isOpen && (
        <CollapsibleContent className={'pb-7'}>
          <div className="d-flex justify-content-end" data-cy="report-pulser-statement-legend">
            <StatementsHeader width={teamsToShow && teamsToShow.length > 0 ? '45%' : '64%'}>
              <StatementsHeaderContent>
                {sortedChartOptions.map((option) => {
                  const optionsWithThisHighlightLevel = sortedChartOptions.filter(
                    (o) => o.highLightLevel === option.highLightLevel,
                  );
                  return (
                    <OptionItem key={option.optionName} data-cy="report-pulser-statement-legend-option">
                      <ColorCircle
                        highlightColor={getAColorByHighlightLevel(
                          option.highLightLevel,
                          optionsWithThisHighlightLevel.length,
                          optionsWithThisHighlightLevel.indexOf(option),
                        )}
                      />
                      <OptionName data-no-break>{option.optionName}</OptionName>
                    </OptionItem>
                  );
                })}
              </StatementsHeaderContent>
            </StatementsHeader>
          </div>
          {statements
            .reduce((acc, statement, statementIndex) => {
              // group statements into groups of 3
              const groupIndex = Math.floor(statementIndex / 3);
              if (!acc[groupIndex]) {
                acc[groupIndex] = [];
              }
              acc[groupIndex].push(statement);
              return acc;
            }, [] as Statement[][])
            .map((statementGroup, statementGroupIndex) => (
              <div key={statementGroupIndex} data-pulser-statement-group-index={statementGroupIndex}>
                {statementGroup.map((statement, statementIndexInGroup) => (
                  <StatementChart
                    key={statementIndexInGroup}
                    statement={statement}
                    chartOptions={chartOptions}
                    compareLevelFilter={compareLevelFilter}
                    teams={teamsToShow}
                    showEscapeOption={question.showEscapeOption ?? true}
                    showComments={!question.isRankingScale}
                  />
                ))}
              </div>
            ))}
        </CollapsibleContent>
      )}
    </QuestionWrapper>
  );
};

const QuestionWrapper = styled.div`
  background: white;
  margin-bottom: ${theme.spacing.c};
`;

const QuestionHeader = styled.div`
  cursor: pointer;
  transition: background-color 0.2s ease;
`;

const IconTextWrapper = styled.div`
  width: 27%;
`;

const WidgetQuestionHeading = styled.div({
  ...omit(['family'], theme.typography.body.medium.bold),
  marginTop: theme.spacing.d,
  marginRight: theme.spacing.e,
  wordBreak: 'keep-all',
});

const SmallPercentageValue = styled.div({
  ...omit(['family'], theme.typography.body.xLarge.bold),
  marginBottom: theme.spacing.b,
  marginRight: theme.spacing.e,
  marginLeft: theme.spacing.i,
  wordBreak: 'keep-all',
});

const CollapsibleContent = styled.div`
  animation: slideDown 0.2s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TeamWrapper = styled.div<{ mobile?: boolean }>`
  display: flex;
  flex-direction: ${({ mobile }) => (mobile ? 'column' : 'row')};
  align-items: ${({ mobile }) => (mobile ? 'flex-start' : 'center')};
  justify-content: ${({ mobile }) => (mobile ? 'flex-start' : 'space-between')};
  gap: ${theme.spacing.d};
  width: 100%;
`;

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.d};
  width: 100%;
`;

export default PulserQuestionItem;
